import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { formatEnergyWh, isReportedEnergyWh } from '@/utils/powerEnergyTransformations'

/** Off-screen but still in the accessibility tree — `display: none` would remove it from both.
 * Same shape as the announcement region in `InteractiveHourlyVolumeChart`. */
const VISUALLY_HIDDEN = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }

type RatedEnergyRowProps = {
  /** Wh, or null when the battery has not reported one. Comes from the owning chart's battery-info
   *  query, so this row and the energy axis are guaranteed to be showing the same number. */
  ratedEnergyWh: number | null
  isError: boolean
  isLoading: boolean
}

/**
 * The battery's rated energy, read-only, as displayed inside the operating-envelope card. It is the
 * value that scales the envelope's energy axis, so without it on screen a user can't tell whether an
 * axis reading "0.10 MWh" means a small battery or a mis-set limit.
 *
 * Presentational: the owning chart holds the query and passes the result down. That keeps one
 * subscription for one request, and makes "the row and the axis agree" a property of the code rather
 * than of two independent fetches happening to match.
 *
 * Deliberately NOT a disabled TextField: the value is telemetry, not a form leaf that happens to be
 * locked, and a greyed input invites "why can't I type here". The tooltip carries the provenance.
 */
export function RatedEnergyRow({ ratedEnergyWh, isError, isLoading }: Readonly<RatedEnergyRowProps>) {
  const { t } = useTranslation()
  const label = t('resources.configuration.envelope.rated_energy')
  const tooltip = t('resources.configuration.envelope.rated_energy_tooltip')

  // A KNOWN VALUE WINS, even alongside an error. TanStack keeps `data` when a later refetch fails, and
  // the axis just below is drawn from that same retained number — so reporting "Could not be
  // read" here while the plot happily ticks 0.00…2.50 MWh would have the card contradict itself. The
  // failure is not swallowed: the shared query hook still raises the app's error alert.
  //
  // Failing WITHOUT a value does say so, rather than "Not reported yet": that would send an operator to
  // the controller team for a platform-side problem, the exact misdiagnosis this row exists to prevent.
  // An in-flight first fetch shows a neutral dash for the same reason — the unknown text is a claim
  // about the hardware we cannot support while the request is still open.
  //
  // That placeholder is a dash for sighted readers and the word "Loading" for everyone else.
  // `aria-busy` alone conveyed nothing: it is a state, not an announcement, so with no live region
  // watching this <dd> a user navigating to the value heard only the em dash — which screen readers
  // render inconsistently and often skip. `role="status"` was the other option and was rejected: it
  // would replace the <dd> role and dismantle the term/definition pairing built below, to announce a
  // read-only configuration value the user is already looking at.
  function renderValue() {
    if (isReportedEnergyWh(ratedEnergyWh)) return formatEnergyWh(ratedEnergyWh)
    if (isError) return t('resources.configuration.envelope.rated_energy_error')
    if (isLoading) {
      return (
        <>
          <Box aria-hidden component="span">
            —
          </Box>
          <Box component="span" sx={VISUALLY_HIDDEN}>
            {t('resources.configuration.envelope.rated_energy_loading')}
          </Box>
        </>
      )
    }
    return t('resources.configuration.envelope.rated_energy_unknown')
  }

  // The help button sits OUTSIDE the <dl>: that content model permits only dt/dd groups, and MUI's
  // Tooltip clones its child rather than wrapping it, so an IconButton placed inside would render as a
  // bare <button> child of the list. Keeping the list to exactly the pair also keeps the help
  // affordance out of the definition's content, where it could be announced as part of the value.
  //
  // The button's accessible name is short and distinct — not the multi-sentence tooltip, which cannot
  // be announced usefully, and not the bare field label, which would read back "Rated energy … 2.50
  // MWh … Rated energy, button".
  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <Stack alignItems="center" component="dl" direction="row" spacing={1} sx={{ my: 0 }}>
        <CustomTypography color="text.secondary" component="dt">
          {label}
        </CustomTypography>
        <CustomTypography aria-busy={isLoading} component="dd" data-testid="rated-energy-value" sx={{ m: 0 }}>
          {renderValue()}
        </CustomTypography>
      </Stack>
      {/* `describeChild` so the prose reaches assistive tech: without it MUI sets neither
       * aria-describedby nor aria-labelledby, because the IconButton's own aria-label wins the name
       * slot, and the provenance this row's value rests on is announced nowhere. */}
      <Tooltip describeChild title={tooltip}>
        <IconButton aria-label={t('resources.configuration.envelope.rated_energy_info_aria')} size="small">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}
